import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const STORAGE_KEY = "ff_pro_celebrated_expiry";

/** One-time "Welcome to Pro" overlay. Shown at most once per account —
 * dismissal is persisted server-side (`pro_celebration_dismissed_at`) so
 * long-time Pro users never see it again on a fresh browser / new device. */
export default function ProUpgradeCelebration() {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!user?.is_pro || !user.subscription_expires_at) return;
    // Server-side dismissal wins — once acknowledged, never show again.
    if (user.pro_celebration_dismissed_at) return;
    // Legacy: users who dismissed pre-server-tracking still have localStorage.
    const lastSeen = localStorage.getItem(STORAGE_KEY);
    if (lastSeen === user.subscription_expires_at) return;
    setOpen(true);
  }, [user?.is_pro, user?.subscription_expires_at, user?.pro_celebration_dismissed_at]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const close = async () => {
    if (user?.subscription_expires_at) {
      localStorage.setItem(STORAGE_KEY, user.subscription_expires_at);
    }
    setOpen(false);
    // Persist server-side so new browsers / devices don't re-trigger the modal.
    try {
      const { data } = await api.post("/me/dismiss-pro-celebration");
      if (typeof setUser === "function" && data?.dismissed_at) {
        setUser((cur) => cur ? { ...cur, pro_celebration_dismissed_at: data.dismissed_at } : cur);
      }
    } catch {
      // best-effort — the localStorage fallback still hides it for this browser
    }
  };

  // ESC-to-dismiss (backdrop click already handled below).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open || !user?.is_pro) return null;

  const expires = new Date(user.subscription_expires_at).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  });

  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-[#2A332C]/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={close}
      data-testid="pro-celebration"
    >
      <Confetti />
      <div
        className="ff-card w-full max-w-md p-8 relative my-auto text-center"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "linear-gradient(135deg, #ffffff 0%, #E8F0E9 100%)" }}
      >
        <button
          onClick={close}
          aria-label="Close"
          data-testid="pro-celebration-close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/70 hover:bg-white text-[#2A332C] flex items-center justify-center"
        >
          ✕
        </button>
        <div className="text-6xl mb-3 animate-bounce">🌱</div>
        <div className="text-xs uppercase tracking-wider font-medium text-[#3B5A3E]">Shop, Cal & Meal Pro is active</div>
        <h2 className="text-3xl font-semibold mt-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          Welcome to Pro, {user.name?.split(" ")[0] || "chef"}! 🎉
        </h2>
        <p className="text-sm text-[#5C6B61] mt-3">
          You&apos;re unlocked until <strong className="text-[#2A332C]">{expires}</strong>.
          Here&apos;s what you can do right now:
        </p>

        <div className="grid grid-cols-1 gap-2 mt-5 text-left">
          <Unlock to="/app/planner" icon="📅" title="Weekly Planner" desc="Plan every meal, track steps, see your net calories" />
          <Unlock to="/app/friends" icon="💚" title="Favourite Friends" desc="Add Pro friends and share your best recipes" />
          <Unlock to="/app/shopping" icon="🛒" title="Unlimited shopping lists" desc="Build a new list every week, auto-reorder ready" />
          <Unlock to="/app/scan" icon="📷" title="Unlimited AI scans" desc="Photo + barcode, with full sugar / salt breakdowns" />
        </div>

        <button
          onClick={close}
          data-testid="pro-celebration-start"
          className="ff-btn-primary text-sm mt-6 w-full"
        >
          Let&apos;s get cooking
        </button>
      </div>
    </div>,
    document.body
  );
}

function Unlock({ to, icon, title, desc }) {
  return (
    <Link
      to={to}
      className="flex items-start gap-3 p-3 rounded-xl bg-white/70 hover:bg-white border border-[#E5E2DA] transition"
      data-testid={`pro-unlock-${to.split("/").pop()}`}
    >
      <span className="text-2xl flex-none">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#2A332C]">{title}</div>
        <div className="text-xs text-[#5C6B61]">{desc}</div>
      </div>
      <span className="text-[#3B5A3E] self-center">→</span>
    </Link>
  );
}

/** CSS-only confetti — small dots fall from the top. */
function Confetti() {
  const colors = ["#3B5A3E", "#C36952", "#F0C657", "#8EB58F", "#E5A57B"];
  const pieces = Array.from({ length: 24 }, (_, i) => i);
  return (
    <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden="true">
      {pieces.map((i) => {
        const left = (i * 4.3) % 100;
        const delay = (i % 8) * 0.18;
        const duration = 2.4 + ((i * 13) % 14) / 10;
        const color = colors[i % colors.length];
        const rotate = (i * 47) % 360;
        return (
          <span
            key={i}
            className="absolute -top-3 w-2.5 h-3.5 rounded-sm"
            style={{
              left: `${left}%`,
              background: color,
              transform: `rotate(${rotate}deg)`,
              animation: `ff-confetti-fall ${duration}s ${delay}s ease-in forwards`,
            }}
          />
        );
      })}
      <style>{`
        @keyframes ff-confetti-fall {
          0%   { transform: translateY(-20px) rotate(0deg);   opacity: 1; }
          80%  { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
